import React, { useState } from "react";
import hosys from "@/api/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle2, Clock, Upload, Loader2, ExternalLink, FileText } from "lucide-react";
import moment from "moment";

const REQUIRED_DOCS = [
  { key: "protocol", label: "Protocol d'assetjament laboral", description: "Document que defineix les actuacions davant casos d'assetjament" },
  { key: "equality", label: "Pla o política d'igualtat", description: "Document de compromís amb la igualtat de tracte i oportunitats" },
  { key: "complaint", label: "Procediment de denúncia", description: "Protocol per formalitzar una denúncia interna" },
];

export default function HarassmentDocuments({ venueId, docs, onDocsChanged, isAdmin }) {
  const [uploadingKey, setUploadingKey] = useState(null);
  const [showUploadFor, setShowUploadFor] = useState(null);
  const [docName, setDocName] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  // Match docs to required types by category+description keyword
  const getDocForKey = (key) => docs.find(d =>
    d.description?.toLowerCase().includes(key) ||
    d.name?.toLowerCase().includes(key === "protocol" ? "protocol" : key === "equality" ? "igualtat" : "denúncia")
  );

  const handleUpload = async () => {
    if (!file || !docName) return;
    setSaving(true);
    const { file_url } = await hosys.integrations.Core.UploadFile({ file });
    await hosys.entities.Document.create({
      venue_id: venueId,
      name: docName,
      category: "protocol",
      description: showUploadFor,
      file_url,
      issue_date: moment().format("YYYY-MM-DD"),
      status: "valid",
    });
    setSaving(false);
    setShowUploadFor(null);
    setDocName("");
    setFile(null);
    onDocsChanged();
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-400">Els documents obligatoris per al compliment en matèria d'assetjament laboral.</p>
      {REQUIRED_DOCS.map(rd => {
        const doc = getDocForKey(rd.key);
        return (
          <Card key={rd.key} className={`p-4 border-0 shadow-sm flex items-center gap-3 ${!doc ? "border-l-4 border-l-amber-300" : "border-l-4 border-l-emerald-400"}`}>
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${doc ? "bg-emerald-100" : "bg-amber-100"}`}>
              {doc ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Clock className="w-4 h-4 text-amber-600" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold ${doc ? "text-gray-800" : "text-gray-600"}`}>{rd.label}</p>
              {doc
                ? <p className="text-xs text-gray-400">{doc.name} · Pujat {moment(doc.created_date).format("DD/MM/YYYY")}</p>
                : <p className="text-xs text-amber-600">{rd.description}</p>
              }
            </div>
            <div className="flex gap-2 flex-shrink-0">
              {doc?.file_url && (
                <a href={doc.file_url} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="h-8 text-xs gap-1"><ExternalLink className="w-3 h-3" /> Obrir</Button>
                </a>
              )}
              {isAdmin && (
                <Button variant={doc ? "outline" : "default"} size="sm"
                  className={`h-8 text-xs gap-1 ${!doc ? "bg-[#7B2D3F] hover:bg-[#5B1D2F]" : ""}`}
                  onClick={() => { setShowUploadFor(rd.key); setDocName(rd.label); }}>
                  <Upload className="w-3 h-3" /> {doc ? "Actualitzar" : "Pujar"}
                </Button>
              )}
            </div>
          </Card>
        );
      })}

      <Dialog open={!!showUploadFor} onOpenChange={o => { if (!o) setShowUploadFor(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Pujar document</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label>Nom del document</Label>
              <Input value={docName} onChange={e => setDocName(e.target.value)} />
            </div>
            <div>
              <Label>Fitxer (PDF, imatge)</Label>
              <label className="mt-1 flex items-center gap-3 cursor-pointer bg-gray-50 border border-dashed border-gray-300 rounded-xl px-4 py-3 hover:bg-gray-100 transition-colors">
                <FileText className="w-5 h-5 text-gray-400" />
                <div className="flex-1">
                  {file ? <span className="text-sm text-gray-700 font-medium">{file.name}</span>
                    : <span className="text-sm text-gray-400">Fes clic per seleccionar fitxer</span>}
                </div>
                <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.docx"
                  onChange={e => setFile(e.target.files?.[0] || null)} />
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadFor(null)}>Cancel·lar</Button>
            <Button onClick={handleUpload} disabled={!file || !docName || saving} className="bg-[#7B2D3F] hover:bg-[#5B1D2F]">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Pujar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}